/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { CollectionIcon, PlusCircleIcon, SwitchHorizontalIcon } from '@heroicons/react/solid'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Badge, Card, Loader, Theme } from '@nexigendigital/nxui'
import { shopAPI } from 'api/shop'
import { ProductServiceType } from 'models/enums'
import { Addon, BaseHosting, PendingItem, Pricing } from 'models/shop'
import { useAppSelector } from 'store/hooks'

import { MemoizedDomains } from 'containers/shop/domains/domains'
import Register from 'containers/shop/domains/register'
import Transfer from 'containers/shop/domains/transfer'
import Existing from 'containers/shop/domains/existing'
import BillingCyclePricing from 'containers/shop/webHosting/billingCyclePricing'
import ConfigOptions from 'containers/shop/webHosting/configOptions'
import Select from 'containers/shop/webHosting/select'
import AddonAction from 'containers/shop/webHosting/addonAction'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function WebHosting() {
    const { items, baseHosting } = useAppSelector((state) => state.shop)
    const { data, isLoading, isSuccess } = shopAPI.endpoints.product.useQueryState(process.env.REACT_APP_WEB_HOSTING)
    const [currentProduct, setCurrentProduct] = useState<BaseHosting | null>(null)
    const tabs = useMemo(() => [
        {
            label: 'Register',
            icon: PlusCircleIcon,
            render: <Register/>
        },
        {
            label: 'Transfer',
            icon: SwitchHorizontalIcon,
            render: <Transfer/>
        },
        {
            label: 'Existing',
            icon: CollectionIcon,
            render: <Existing/>
        }
    ], [])
    const hostingColumn = useMemo(() => [
        {
            Header: 'Plan',
            accessor: 'plan'
        },
        {
            Header: 'Billing Cycle',
            accessor: 'billingCycle'
        },
        {
            Header: 'Cloud Storage',
            accessor: 'cloudStorage'
        },
        {
            Header: 'Premium Bandwidth',
            accessor: 'premiumBandwidth'
        },
        {
            Header: 'CPU Allowance',
            accessor: 'cpuAllowance'
        },
        {
            Header: 'Memory Allowance',
            accessor: 'memoryAllowance'
        },
        {
            Header: '',
            accessor: 'action',
            align: 'right'
        }
    ], [])
    const addonsColumn = useMemo(() => [
        {
            Header: 'Item',
            accessor: 'item'
        },
        {
            Header: 'Description',
            accessor: 'description'
        },
        {
            Header: 'Price',
            accessor: 'price'
        },
        {
            Header: '',
            accessor: 'action',
            align: 'right'
        }
    ], [])

    useEffect(() => {
        if (isSuccess) {
            setCurrentProduct(baseHosting)
        }
    }, [baseHosting, isSuccess])

    if (isLoading) {
        return <Loader/>
    }

    function addonPricing(pricing: Pricing[]) {
        const currentProductPricing = pricing.find(({ billing_cycle: { id } }) => id === currentProduct?.billing_cycle_id)

        return currentProductPricing?.price
    }

    if (isSuccess && currentProduct && data) {
        const { id: product_id, name, pricing, config_option_groups, addons } = data
        const isItemAdded = items.some((item) => item.product_id === currentProduct.product_id && !item.name)
        const { config_options } = config_option_groups[0]
        const diskSpace = config_options.find(({ name: configName }) => configName === 'Disk Space')
        const cpu = config_options.find(({ name: configName }) => configName === 'CPU')
        const memory = config_options.find(({ name: configName }) => configName === 'Memory')

        const hostingTable = [{
            plan: name,
            billingCycle: (
                <BillingCyclePricing
                    productID={product_id}
                    pricing={pricing}
                    configOptions={config_options}
                    currentProduct={currentProduct}
                    setCurrentProduct={setCurrentProduct}
                />
            ),
            cloudStorage: (
                <ConfigOptions
                    productID={product_id}
                    productConfig={diskSpace}
                    items={items}
                    currentProduct={currentProduct}
                    setCurrentProduct={setCurrentProduct}
                />
            ),
            premiumBandwidth: <BadgeWrapper><Badge>Unlimited</Badge></BadgeWrapper>,
            cpuAllowance: (
                <ConfigOptions
                    productID={product_id}
                    productConfig={cpu}
                    items={items}
                    currentProduct={currentProduct}
                    setCurrentProduct={setCurrentProduct}
                />
            ),
            memoryAllowance: (
                <ConfigOptions
                    productID={product_id}
                    productConfig={memory}
                    items={items}
                    currentProduct={currentProduct}
                    setCurrentProduct={setCurrentProduct}
                />
            ),
            action: (
                <Select
                    isItemAdded={isItemAdded}
                    currentProduct={currentProduct}
                    setCurrentProduct={setCurrentProduct}
                />
            )
        }]

        const addonsTable = [
            ...addons.map((addon: Addon) => {
                const price = addonPricing(addon.pricing)
                const isAddonAdded = items.some((item) => {
                    if (item.product_id === currentProduct.product_id) {
                        return item.addons.some((cartItemAddon) => cartItemAddon.product_id === addon.id)
                    }

                    return false
                })

                return ({
                    price,
                    item: addon.name,
                    description: addon.description,
                    action: (
                        <AddonAction
                            productID={product_id}
                            price={price}
                            addon={addon}
                            isItemAdded={isItemAdded}
                            isAddonAdded={isAddonAdded}
                            pricing={addon.pricing}
                        />
                    )
                })
            })
        ]

        return (
            <WebHostingWrapper>
                <Card
                    title={'Web Hosting'}
                    table={{
                        render: {
                            columns: () => hostingColumn,
                            data: () => hostingTable
                        },
                        conditions: {
                            sort: false
                        }
                    }}
                />
                {addons.length >= 1 &&
                    <Card
                        title={'Addons'}
                        table={{
                            render: {
                                columns: () => items.some((item: PendingItem) => item.product_type === ProductServiceType.SharedHosting) ? addonsColumn : [],
                                data: () => items.some((item: PendingItem) => item.product_type === ProductServiceType.SharedHosting) ? addonsTable : []
                            }
                        }}
                    />
                }
                {items.some((item: PendingItem) => item.product_type === ProductServiceType.SharedHosting) &&
                    <MemoizedDomains tabs={tabs}/>
                }
            </WebHostingWrapper>
        )
    }

    return null
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const WebHostingWrapper = styled.section`
    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-xl']))} {
        margin-top: 20px
    }
`

const BadgeWrapper = styled.div`
    justify-content: center;
    text-align: center;
`
